import React, { useState } from 'react';
import { Button, Popconfirm, message, Tooltip } from "antd";
import { CalendarOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { useQueryClient } from "react-query";
import axios from "axios";
import { InvitationStatus } from "../../commons/data/types";

export interface ScheduleInvitationButtonProps {
    invitationId?: string;
    status?: InvitationStatus;
    sessionsCount?: number;
    size?: 'small' | 'middle' | 'large';
    style?: React.CSSProperties;
    buttonText?: string;
    onScheduled?: () => void;
}

export const ScheduleInvitationButton: React.FC<ScheduleInvitationButtonProps> = ({
    invitationId,
    status = 'UNPLANNED',
    sessionsCount = 0,
    size = 'large',
    style,
    buttonText,
    onScheduled
}) => {
    const { t } = useTranslation();
    const queryClient = useQueryClient();
    const [loading, setLoading] = useState<boolean>(false);

    if (!invitationId || status === 'SCHEDULED' || status === 'CANCELLED') {
        return null;
    }

    const hasNoSessions = sessionsCount === 0;

    const handleSchedule = async () => {
        setLoading(true);
        try {
            await axios.post(`/api/organizer/invitations/${invitationId}/schedule`);
            message.success(t('organizer.invitations.scheduleSuccess', 'Invitation scheduled successfully! Session reservations created and SMS notifications dispatched.'));
            queryClient.invalidateQueries(['public-invitation-detail', invitationId]);
            queryClient.invalidateQueries(['organizer-invitation', invitationId]);
            queryClient.invalidateQueries(['organizer-invitations']);
            queryClient.invalidateQueries(['organizer-join-requests', invitationId]);
            onScheduled?.();
        } catch (err: any) {
            message.error(err?.response?.data || err?.message || t('organizer.invitations.scheduleError', 'Failed to schedule invitation.'));
        } finally {
            setLoading(false);
        }
    };

    const buttonElement = (
        <Button
            type="primary"
            size={size}
            icon={<CalendarOutlined />}
            loading={loading}
            disabled={hasNoSessions}
            style={{
                backgroundColor: hasNoSessions ? undefined : '#52c41a',
                borderColor: hasNoSessions ? undefined : '#52c41a',
                minWidth: size === 'large' ? 200 : undefined,
                ...style
            }}
        >
            {buttonText || t('organizer.invitations.scheduleButton', 'Schedule Invitation')}
        </Button>
    );

    if (hasNoSessions) {
        return (
            <Tooltip title={t('organizer.invitations.needSessionsToSchedule', 'Please add at least one session date before scheduling this invitation.')}>
                <span>{buttonElement}</span>
            </Tooltip>
        );
    }

    return (
        <Popconfirm
            title={t('organizer.invitations.scheduleConfirmTitle', 'Schedule this invitation?')}
            description={t('organizer.invitations.scheduleConfirmDesc', 'This will change the invitation status to SCHEDULED, create session reservations for all approved players, and send them an SMS notification.')}
            onConfirm={handleSchedule}
            okText={t('organizer.invitations.scheduleConfirmOk', 'Yes, Schedule')}
            cancelText={t('common.cancel', 'Cancel')}
        >
            {buttonElement}
        </Popconfirm>
    );
};
